import React, { useState, useEffect } from 'react';
import { Calendar, Phone, Plane, Users, Search, Globe, Clock, Heart, Shield, Info, HandHeart, Home } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Simple Router Component
const Router = ({ children }) => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname || '/home');

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };

  return (
    <RouterContext.Provider value={{ currentPath, navigate }}>
      {children}
    </RouterContext.Provider>
  );
};

const RouterContext = React.createContext({
  currentPath: '/home',
  navigate: () => {}
});

const useNavigate = () => {
  const context = React.useContext(RouterContext);
  return context.navigate;
};

const useLocation = () => {
  const context = React.useContext(RouterContext);
  return { pathname: context.currentPath };
};

// Header Component
const Header = () => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-8 px-4 shadow-lg">
      <div className="max-w-6xl mx-auto text-center">
        <h1 
          className="text-4xl font-bold mb-2 flex items-center justify-center gap-3 cursor-pointer"
          onClick={() => navigate('/home')}
        >
          <Plane className="w-10 h-10" />
          Travel Companion Finder
        </h1>
        <p className="text-xl opacity-90">Connecting elderly travelers with caring companions</p>
      </div>
    </div>
  );
};

// Footer Component
const Footer = () => (
  <div className="mt-16 bg-gray-800 text-white py-8 px-4">
    <div className="max-w-6xl mx-auto text-center">
      <p className="mb-2">Travel Companion Finder © 2025</p>
      <p className="text-gray-400 text-sm">Helping elderly travelers journey safely with care and companionship</p>
    </div>
  </div>
);

// Home Page Component
const HomePage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="max-w-6xl mx-auto p-4 mt-8">
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 flex flex-col">
          <div className="flex items-center justify-center mb-4">
            <HandHeart className="w-16 h-16 text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-center mb-3">I Want to Help</h2>
          <p className="text-gray-600 text-center mb-6 flex-grow">Register as a travel companion to assist elderly travelers on their journey</p>
          <button
            onClick={() => navigate('/register')}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 border-2 border-purple-700"
          >
            Register Now Now
          </button>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 flex flex-col">
          <div className="flex items-center justify-center mb-4">
            <Search className="w-16 h-16 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-center mb-3">Find a Companion</h2>
          <p className="text-gray-600 text-center mb-6 flex-grow">Search for travelers to accompany your elderly relatives</p>
          <button
            onClick={() => navigate('/search')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 border-2 border-blue-700"
          >
            Search Companions
          </button>
        </div>
      </div>

      {/* Important Disclaimer */}
      <div className="mt-6 bg-amber-50 rounded-xl p-6 border-2 border-amber-200">
        <h3 className="text-lg font-bold text-amber-800 mb-3 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Important Information
        </h3>
        <div className="space-y-3 text-sm text-amber-700">
          <p>
            <strong>Community-Based Platform:</strong> This application operates on a crowd-sourced model. 
            All companion information is voluntarily provided by individuals registering themselves as travel companions. 
            We do not receive or verify data from airlines or other official sources.
          </p>
          <p>
            <strong>Your Safety Matters:</strong> Before arranging travel assistance for your elderly loved ones, 
            we strongly encourage you to:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Verify the companion's identity through a video call or in-person meeting</li>
            <li>Cross-check their flight details independently</li>
            <li>Share companion details with multiple family members</li>
            <li>Consider meeting at the airport before the travel date if possible</li>
            <li>Trust your instincts - if something feels wrong, look for alternatives</li>
          </ul>
          <p className="pt-2 font-medium">
            While we facilitate connections between travelers, the responsibility for verification and safety 
            remains with users. Please exercise appropriate caution and judgment.
          </p>
        </div>
      </div>

      <br />

      {/* Instruction Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
          <h3 className="text-lg font-bold text-purple-800 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            For Help Providers
          </h3>
          <ul className="space-y-2 text-sm text-purple-700">
            <li className="flex items-start gap-2">
              <Heart className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Thank you for your intent to help elderly travelers</span>
            </li>
            <li className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Please provide genuine and accurate information</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>This platform is created to help elderly with good intentions</span>
            </li>
            <li className="flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Your assistance can make their journey safer and comfortable</span>
            </li>
            <li className="flex items-start gap-2">
              <Heart className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Be patient and understanding with elderly travelers</span>
            </li>
          </ul>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
          <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5" />
            For Help Seekers
          </h3>
          <ul className="space-y-2 text-sm text-blue-700">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Please contact companions only if genuinely needed</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Contact only via their preferred method and hours</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Respect their time, space, and boundaries</span>
            </li>
            <li className="flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Verify companion details before finalizing travel plans</span>
            </li>
            <li className="flex items-start gap-2">
              <Heart className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Express gratitude for their willingness to help</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Feedback Card */}
      <div className="mt-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">Share Feedback & Suggestions</h3>
        <p className="text-sm text-gray-600 text-center mb-4">
          Your feedback helps me improve this platform for everyone. We'd love to hear your thoughts!
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="mailto:travelcompanionsupport@gmail.com?subject=Feedback%20for%20Travel%20Companion%20Finder&body=Hi%2C%0A%0AI%20would%20like%20to%20share%20the%20following%20feedback%3A%0A%0A"
            className="flex flex-col items-center gap-1"
          >
            <div className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Support
            </div>
            <span className="text-xs text-gray-600">travelcompanionsupport@gmail.com</span>
          </a>
        </div>
      </div>
    </div>
  );
};

// Register Page Component
const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [customAirportCode, setCustomAirportCode] = useState('');
  const [modalType, setModalType] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [companionForm, setCompanionForm] = useState({
    name: '',
    phone: '',
    flightNumber: '',
    fromAirport: '',
    toAirport: '',
    travelDate: '',
    preferredContactMethod: 'call',
    preferredContactHours: ''
  });

  const indianAirports = [
    { code: 'DEL', name: 'Delhi - Indira Gandhi International', country: 'India' },
    { code: 'BOM', name: 'Mumbai - Chhatrapati Shivaji International', country: 'India' },
    { code: 'BLR', name: 'Bengaluru - Kempegowda International', country: 'India' },
    { code: 'MAA', name: 'Chennai - Chennai International', country: 'India' },
    { code: 'CCU', name: 'Kolkata - Netaji Subhas Chandra Bose International', country: 'India' },
    { code: 'HYD', name: 'Hyderabad - Rajiv Gandhi International', country: 'India' },
    { code: 'COK', name: 'Kochi - Cochin International', country: 'India' },
    { code: 'AMD', name: 'Ahmedabad - Sardar Vallabhbhai Patel International', country: 'India' },
    { code: 'GOI', name: 'Goa - Dabolim Airport', country: 'India' },
    { code: 'JAI', name: 'Jaipur - Jaipur International', country: 'India' }
  ];

  const usAirports = [
    { code: 'JFK', name: 'New York - John F. Kennedy International', country: 'USA' },
    { code: 'LAX', name: 'Los Angeles - Los Angeles International', country: 'USA' },
    { code: 'ORD', name: 'Chicago - O\'Hare International', country: 'USA' },
    { code: 'SFO', name: 'San Francisco - San Francisco International', country: 'USA' },
    { code: 'ATL', name: 'Atlanta - Hartsfield-Jackson International', country: 'USA' },
    { code: 'DFW', name: 'Dallas - Dallas/Fort Worth International', country: 'USA' },
    { code: 'SEA', name: 'Seattle - Seattle-Tacoma International', country: 'USA' },
    { code: 'BOS', name: 'Boston - Logan International', country: 'USA' },
    { code: 'IAD', name: 'Washington DC - Dulles International', country: 'USA' },
    { code: 'EWR', name: 'Newark - Newark Liberty International', country: 'USA' },
    { code: 'MIA', name: 'Miami - Miami International', country: 'USA' },
    { code: 'LAS', name: 'Las Vegas - McCarran International', country: 'USA' }
  ];

  const allAirports = [...indianAirports, ...usAirports];

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\+[1-9]\d{7,14}$/;
    if (!phone) {
      return 'Phone number is required';
    }
    if (!phone.startsWith('+')) {
      return 'Phone number must include country code starting with +';
    }
    if (!/^[+\d]+$/.test(phone)) {
      return 'Phone number can only contain + and digits (no spaces or other characters)';
    }
    if (!phoneRegex.test(phone)) {
      return 'Invalid phone format. Use +[country code][number] (minimum 9 digits total)';
    }
    if (phone.length < 9 || phone.length > 16) {
      return 'Phone number must be between 9-16 characters including country code';
    }
    return null;
  };

  const validateFlightNumber = (flightNumber) => {
    const flightRegex = /^[A-Z]{2,3}[0-9]{1,4}$/;
    if (!flightNumber) {
      return 'Flight number is required';
    }
    const upperFlight = flightNumber.toUpperCase().replace(/\s/g, '');
    if (!flightRegex.test(upperFlight)) {
      return 'Invalid flight format. Use airline code (2-3 letters) + flight number (1-4 digits). Example: AI127, UA1234';
    }
    return null;
  };

  const validateName = (name) => {
    if (!name || name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (name.trim().length > 255) {
      return 'Name must be less than 255 characters';
    }
    return null;
  };

  const validateField = (fieldName, value) => {
    let error = null;
    
    switch(fieldName) {
      case 'name':
        error = validateName(value);
        break;
      case 'phone':
        error = validatePhoneNumber(value);
        break;
      case 'flightNumber':
        error = validateFlightNumber(value);
        break;
      case 'fromAirport':
        if (!value) error = 'Departure airport is required';
        break;
      case 'toAirport':
        if (!value) error = 'Destination airport is required';
        if (value && companionForm.fromAirport && value === companionForm.fromAirport) {
          error = 'Destination airport must be different from departure airport';
        }
        break;
      case 'travelDate':
        if (!value) {
          error = 'Travel date is required';
        } else if (new Date(value) <= new Date()) {
          error = 'Travel date must be in the future';
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  const updateFormField = (field, value) => {
    setCompanionForm({...companionForm, [field]: value});
    
    const error = validateField(field, value);
    
    if (error) {
      setFormErrors({...formErrors, [field]: error});
    } else {
      const newErrors = {...formErrors};
      delete newErrors[field];
      
      if (field === 'fromAirport' && companionForm.toAirport) {
        const toAirportError = validateField('toAirport', companionForm.toAirport);
        if (!toAirportError) {
          delete newErrors.toAirport;
        }
      }
      
      setFormErrors(newErrors);
    }
  };

  const hasFormErrors = () => {
    const requiredFields = ['name', 'phone', 'flightNumber', 'fromAirport', 'toAirport', 'travelDate'];
    
    if (Object.keys(formErrors).length > 0) {
      return true;
    }
    
    for (const field of requiredFields) {
      if (!companionForm[field]) {
        return true;
      }
    }
    
    return false;
  };

  const handleCustomAirportSubmit = () => {
    const code = customAirportCode.toUpperCase().trim();
    if (code && code.length === 3 && /^[A-Z]{3}$/.test(code)) {
      if (modalType === 'companionFrom') {
        updateFormField('fromAirport', code);
      } else if (modalType === 'companionTo') {
        updateFormField('toAirport', code);
      }
      setShowModal(false);
      setCustomAirportCode('');
    } else {
      alert('Please enter a valid 3-letter airport code (e.g., LAX, DEL)');
    }
  };

  const openCustomAirportModal = (type) => {
    setModalType(type);
    setCustomAirportCode('');
    setShowModal(true);
  };

  const handleCompanionSubmit = async () => {
    setLoading(true);

    try {
      const formDataToSend = {
        ...companionForm,
        travelDate: companionForm.travelDate + 'T00:00:00'
      };

      const response = await fetch(`${API_BASE_URL}/companions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formDataToSend),
      });

      if (response.ok) {
        setSuccessMessage('🎉 Registration successful! Thank you for your willingness to help elderly travelers. Your kindness makes a difference!');
        setCompanionForm({
          name: '',
          phone: '',
          flightNumber: '',
          fromAirport: '',
          toAirport: '',
          travelDate: '',
          preferredContactMethod: 'call',
          preferredContactHours: ''
        });
        setFormErrors({});
        setTermsAccepted(false);
        
        setTimeout(() => setSuccessMessage(''), 7000);
      } else {
        const error = await response.json();
        if (error.errors && Array.isArray(error.errors)) {
          const backendErrors = {};
          error.errors.forEach(err => {
            const field = err.param;
            backendErrors[field] = err.msg;
          });
          setFormErrors(backendErrors);
        } else {
          alert(`Error: ${error.error || 'Failed to register'}`);
        }
      }
    } catch (error) {
      alert('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-6xl mx-auto p-4 mt-8">
      {successMessage && (
        <div className="bg-green-100 border-2 border-green-400 text-green-800 px-6 py-4 rounded-lg mb-6">
          <p className="text-lg font-semibold">{successMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Register as a Travel Companion</h2>
          <button 
            onClick={() => navigate('/home')}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
          >
            <Home className="w-4 h-4" />
            <span>Home</span>
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <div className="block text-sm font-semibold text-gray-700 mb-2">Full Name</div>
            <input
              type="text"
              value={companionForm.name}
              onChange={(e) => updateFormField('name', e.target.value)}
              placeholder="Enter your full name"
              required
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                formErrors.name 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:border-purple-500'
              }`}
            />
            {formErrors.name && (
              <p className="text-red-600 text-sm mt-1">⚠️ {formErrors.name}</p>
            )}
          </div>

          <div>
            <div className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</div>
            <input
              type="tel"
              value={companionForm.phone}
              onChange={(e) => updateFormField('phone', e.target.value)}
              placeholder="+12345678901 (include country code)"
              required
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                formErrors.phone 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:border-purple-500'
              }`}
            />
            {formErrors.phone && (
              <p className="text-red-600 text-sm mt-1">⚠️ {formErrors.phone}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Format: +[country code][number] (minimum 9 digits total, only + and digits allowed)</p>
          </div>

          <div>
            <div className="block text-sm font-semibold text-gray-700 mb-2">Flight Number</div>
            <input
              type="text"
              value={companionForm.flightNumber}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                updateFormField('flightNumber', value);
              }}
              placeholder="AI127, UA1234, etc."
              required
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors uppercase ${
                formErrors.flightNumber 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:border-purple-500'
              }`}
            />
            {formErrors.flightNumber && (
              <p className="text-red-600 text-sm mt-1">⚠️ {formErrors.flightNumber}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Format: Airline code (2-3 letters) + Flight number (1-4 digits)</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="block text-sm font-semibold text-gray-700 mb-2">From Airport</div>
              <select
                value={companionForm.fromAirport}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    openCustomAirportModal('companionFrom');
                  } else {
                    updateFormField('fromAirport', e.target.value);
                  }
                }}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  formErrors.fromAirport 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:border-purple-500'
                }`}
                required
              >
                <option value="">Select departure airport</option>
                <optgroup label="🇮🇳 India">
                  {indianAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="🇺🇸 USA">
                  {usAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <option value="custom">➕ Other Airport (Enter Code)</option>
              </select>
              {formErrors.fromAirport && (
                <p className="text-red-600 text-sm mt-1">⚠️ {formErrors.fromAirport}</p>
              )}
              {companionForm.fromAirport && !allAirports.find(a => a.code === companionForm.fromAirport) && (
                <p className="text-sm text-gray-600 mt-1">Custom airport: {companionForm.fromAirport}</p>
              )}
            </div>

            <div>
              <div className="block text-sm font-semibold text-gray-700 mb-2">To Airport</div>
              <select
                value={companionForm.toAirport}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    openCustomAirportModal('companionTo');
                  } else {
                    updateFormField('toAirport', e.target.value);
                  }
                }}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  formErrors.toAirport 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:border-purple-500'
                }`}
                required
              >
                <option value="">Select destination airport</option>
                <optgroup label="🇮🇳 India">
                  {indianAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="🇺🇸 USA">
                  {usAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <option value="custom">➕ Other Airport (Enter Code)</option>
              </select>
              {formErrors.toAirport && (
                <p className="text-red-600 text-sm mt-1">⚠️ {formErrors.toAirport}</p>
              )}
              {companionForm.toAirport && !allAirports.find(a => a.code === companionForm.toAirport) && (
                <p className="text-sm text-gray-600 mt-1">Custom airport: {companionForm.toAirport}</p>
              )}
            </div>
          </div>

          <div>
            <div className="block text-sm font-semibold text-gray-700 mb-2">Travel Date</div>
            <input
              type="date"
              value={companionForm.travelDate}
              onChange={(e) => updateFormField('travelDate', e.target.value)}
              min={today}
              required
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                formErrors.travelDate 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:border-purple-500'
              }`}
            />
            {formErrors.travelDate && (
              <p className="text-red-600 text-sm mt-1">⚠️ {formErrors.travelDate}</p>
            )}
          </div>

          <div>
            <div className="block text-sm font-semibold text-gray-700 mb-2">Preferred Contact Method</div>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="call"
                  checked={companionForm.preferredContactMethod === 'call'}
                  onChange={(e) => setCompanionForm({...companionForm, preferredContactMethod: e.target.value})}
                  className="mr-2"
                />
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Phone Call
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="text"
                  checked={companionForm.preferredContactMethod === 'text'}
                  onChange={(e) => setCompanionForm({...companionForm, preferredContactMethod: e.target.value})}
                  className="mr-2"
                />
                <span>Text Message</span>
              </label>
            </div>
          </div>

          <div>
            <div className="block text-sm font-semibold text-gray-700 mb-2">Preferred Contact Hours</div>
            <input
              type="text"
              value={companionForm.preferredContactHours}
              onChange={(e) => setCompanionForm({...companionForm, preferredContactHours: e.target.value})}
              placeholder="e.g., 9 AM - 6 PM EST, or anytime except nights"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none transition-colors"
            />
            <p className="text-xs text-gray-500 mt-1">Optional - specify your available hours and timezone</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 mr-3"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-purple-600 hover:text-purple-700 underline"
                >
                  Terms and Conditions for Travel Companions
                </button>
              </span>
            </label>
          </div>

          <button
            onClick={handleCompanionSubmit}
            disabled={loading || hasFormErrors() || !termsAccepted}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Registering...' : 'Register as Companion'}
          </button>
        </div>
      </div>

      {/* Custom Airport Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Plane className="w-6 h-6 text-blue-600" />
                  Enter Airport Code
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Please enter the 3-letter airport code (e.g., LAX, DEL, LHR)
                </p>
                <input
                  type="text"
                  value={customAirportCode}
                  onChange={(e) => setCustomAirportCode(e.target.value)}
                  placeholder="Enter airport code"
                  maxLength={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-center text-lg font-mono uppercase"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCustomAirportSubmit();
                    }
                  }}
                  autoFocus
                />
                <p className="text-sm text-gray-500 mt-2">
                  Examples: JFK (New York), LAX (Los Angeles), DEL (Delhi)
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomAirportSubmit}
                  disabled={!customAirportCode.trim() || customAirportCode.trim().length !== 3}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Airport
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal for Companions */}
      {/* {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 transform transition-all max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-800">Terms and Conditions for Travel Companions</h3>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="space-y-4 text-gray-700">
                <h4 className="font-bold text-lg">1. Purpose and Commitment</h4>
                <p>By registering as a travel companion, you acknowledge that this platform is designed to facilitate assistance for elderly travelers who need support during their journey. Your participation is voluntary and based on humanitarian goodwill.</p>

                <h4 className="font-bold text-lg">2. Information Accuracy</h4>
                <p>You certify that all information provided in your registration is true, accurate, and current. Any false information may result in immediate removal from the platform and potential legal consequences.</p>

                <h4 className="font-bold text-lg">3. Responsibilities as a Companion</h4>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Provide reasonable assistance to elderly travelers during the shared journey</li>
                  <li>Be present at the designated meeting point at the agreed time</li>
                  <li>Assist with basic needs such as navigating the airport, handling luggage within reason, and ensuring the elderly person boards safely</li>
                  <li>Maintain respectful and patient behavior throughout the interaction</li>
                  <li>Respect the elderly person's independence and dignity</li>
                </ul>

                <h4 className="font-bold text-lg">4. Limitations of Service</h4>
                <ul className="list-disc ml-6 space-y-2">
                  <li>You are NOT responsible for medical care or medical emergencies</li>
                  <li>You are NOT required to provide financial assistance</li>
                  <li>You are NOT obligated to change your travel plans</li>
                  <li>You may decline requests that make you uncomfortable</li>
                </ul>

                <h4 className="font-bold text-lg">5. Safety and Verification</h4>
                <p>You understand that users will verify your identity before travel. You agree to participate in reasonable verification processes, which may include video calls or meeting at the airport before travel.</p>

                <h4 className="font-bold text-lg">6. Privacy and Communication</h4>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Your contact information will only be shared with users who match your travel criteria</li>
                  <li>You have the right to decline any request</li>
                  <li>All communication should remain respectful and relevant to travel assistance</li>
                  <li>You may report any inappropriate behavior to platform administrators</li>
                </ul>

                <h4 className="font-bold text-lg">7. Liability Disclaimer</h4>
                <p>This platform serves only as a connection service. You acknowledge that:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>The platform is not responsible for any incidents during travel</li>
                  <li>You participate at your own discretion and risk</li>
                  <li>The platform does not conduct background checks</li>
                  <li>You should use your own judgment in all interactions</li>
                </ul>

                <h4 className="font-bold text-lg">8. Code of Conduct</h4>
                <p>You agree to:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Treat all elderly travelers with respect, patience, and kindness</li>
                  <li>Not discriminate based on race, religion, gender, or nationality</li>
                  <li>Not solicit money or gifts from elderly travelers or their families</li>
                  <li>Maintain appropriate boundaries in all interactions</li>
                  <li>Report any safety concerns immediately</li>
                </ul>

                <h4 className="font-bold text-lg">9. Termination</h4>
                <p>Your registration may be terminated if you violate these terms or receive legitimate complaints about your conduct.</p>

                <h4 className="font-bold text-lg">10. Acceptance</h4>
                <p>By checking the acceptance box and registering, you confirm that you have read, understood, and agree to abide by these terms and conditions.</p>
              </div>
            </div>
            
            <div className="p-6 border-t">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )} */}
      {showTermsModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 transform transition-all max-h-[90vh] overflow-hidden flex flex-col">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-800">Travel Companion Terms of Service</h3>
          <button
            onClick={() => setShowTermsModal(false)}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
      </div>
      
      <div className="p-6 overflow-y-auto flex-grow">
        <div className="space-y-4 text-gray-700">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-amber-900">
              IMPORTANT NOTICE: By registering as a Travel Companion, you agree to these Terms of Service. 
              You must be 18 years or older to participate. Last updated: {new Date().toLocaleDateString()}.
            </p>
          </div>

          <h4 className="font-bold text-lg">1. Definitions and Platform Overview</h4>
          <p>
            <strong>1.1</strong> "Platform" refers to this website and associated services.<br/>
            <strong>1.2</strong> "Companion" refers to you, the individual offering travel assistance.<br/>
            <strong>1.3</strong> "Traveler" refers to elderly individuals seeking travel assistance.<br/>
            <strong>1.4</strong> "Service" refers to informal travel assistance during shared journeys.<br/>
            <strong>1.5</strong> The Platform operates solely as a connection service and does not employ, supervise, or control Companions.
          </p>

          <h4 className="font-bold text-lg">2. Eligibility and Registration Requirements</h4>
          <p>
            <strong>2.1</strong> You must be at least 18 years of age.<br/>
            <strong>2.2</strong> You must have legal authorization to travel to all intended destinations.<br/>
            <strong>2.3</strong> All information provided must be truthful, accurate, and current.<br/>
            <strong>2.4</strong> You must promptly update any changes to your registration information.<br/>
            <strong>2.5</strong> Providing false information constitutes fraud and may result in legal action.
          </p>

          <h4 className="font-bold text-lg">3. Background and Disqualifications</h4>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="font-semibold text-red-900">You are PROHIBITED from registering if you:</p>
            <ul className="list-disc ml-6 space-y-1 text-red-800">
              <li>Are a registered sex offender or have convictions involving minors</li>
              <li>Have been convicted of elder abuse, neglect, or exploitation</li>
              <li>Have convictions for fraud, theft, or financial crimes</li>
              <li>Are under investigation for any violent crimes</li>
              <li>Have active warrants or legal travel restrictions</li>
              <li>Have been diagnosed with conditions that impair judgment or behavior control</li>
            </ul>
          </div>

          <h4 className="font-bold text-lg">4. Scope of Companion Services</h4>
          <p className="font-semibold">As a Companion, you agree to provide:</p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Basic assistance navigating airports and travel terminals</li>
            <li>Help with reasonable luggage handling (within your physical capabilities)</li>
            <li>Companionship and reassurance during the journey</li>
            <li>Assistance ensuring the Traveler boards correct flights/transportation</li>
            <li>Basic communication help if language barriers exist</li>
            <li>Alert relevant personnel in case of apparent distress or emergency</li>
          </ul>

          <h4 className="font-bold text-lg">5. Express Limitations and Disclaimers</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="font-semibold text-blue-900">You are NOT responsible for and should NOT:</p>
            <ul className="list-disc ml-6 space-y-1 text-blue-800">
              <li>Provide medical care, administer medications, or make medical decisions</li>
              <li>Handle medical emergencies beyond calling for professional help</li>
              <li>Provide financial assistance or handle Traveler's money/valuables</li>
              <li>Make legal decisions or sign documents on Traveler's behalf</li>
              <li>Provide intimate personal care or assistance with toileting</li>
              <li>Deviate from your planned travel route or extend your journey</li>
              <li>Accept power of attorney or guardianship responsibilities</li>
            </ul>
          </div>

          <h4 className="font-bold text-lg">6. Verification and Safety Protocols</h4>
          <p>
            <strong>6.1</strong> You consent to identity verification by Travelers and their families.<br/>
            <strong>6.2</strong> You agree to participate in pre-travel video calls or meetings.<br/>
            <strong>6.3</strong> You must provide valid government-issued ID upon request.<br/>
            <strong>6.4</strong> You authorize the Platform to verify your identity and travel documents.<br/>
            <strong>6.5</strong> You understand families may conduct independent background checks.
          </p>

          <h4 className="font-bold text-lg">7. Code of Conduct and Professional Standards</h4>
          <p>You agree to:</p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Maintain professional, respectful behavior at all times</li>
            <li>Respect Traveler's dignity, privacy, and independence</li>
            <li>Not discriminate based on protected characteristics</li>
            <li>Not solicit money, gifts, or benefits from Travelers or families</li>
            <li>Not engage in any romantic or sexual conduct with Travelers</li>
            <li>Not use information gained for any purpose beyond the specific journey</li>
            <li>Report any safety concerns to appropriate authorities immediately</li>
          </ul>

          <h4 className="font-bold text-lg">8. Financial Arrangements</h4>
          <p>
            <strong>8.1</strong> Any financial arrangements are strictly between you and the Traveler/family.<br/>
            <strong>8.2</strong> The Platform does not set rates or handle payments.<br/>
            <strong>8.3</strong> You are responsible for your own tax obligations.<br/>
            <strong>8.4</strong> You may not request payment beyond agreed-upon arrangements.<br/>
            <strong>8.5</strong> All financial agreements should be documented in writing.
          </p>

          <h4 className="font-bold text-lg">9. Privacy and Data Protection</h4>
          <p>
            <strong>9.1</strong> Your profile information will be visible to registered searchers.<br/>
            <strong>9.2</strong> Contact information is shared only with matched Travelers.<br/>
            <strong>9.3</strong> You may not use Traveler information for marketing or solicitation.<br/>
            <strong>9.4</strong> You must delete Traveler contact information after journey completion.<br/>
            <strong>9.5</strong> The Platform may share your information with law enforcement if required.
          </p>

          <h4 className="font-bold text-lg">10. Insurance and Liability</h4>
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
            <p className="font-semibold text-yellow-900">Important Insurance Information:</p>
            <ul className="list-disc ml-6 space-y-1 text-yellow-800">
              <li>The Platform does NOT provide insurance coverage</li>
              <li>You are advised to maintain appropriate personal liability insurance</li>
              <li>You should verify your insurance covers volunteer assistance activities</li>
              <li>You are personally liable for any damages caused by your negligence</li>
            </ul>
          </div>

          <h4 className="font-bold text-lg">11. Indemnification</h4>
          <p>
            You agree to indemnify, defend, and hold harmless the Platform, its officers, directors, 
            employees, and agents from any claims, damages, losses, liabilities, and expenses (including 
            legal fees) arising from: (a) your use of the Platform, (b) your provision of companion 
            services, (c) your violation of these Terms, (d) your violation of any third-party rights, 
            or (e) any negligent or wrongful conduct.
          </p>

          <h4 className="font-bold text-lg">12. Limitation of Liability</h4>
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
            <p className="text-sm">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR 
              PARTICIPATION AS A COMPANION. THE PLATFORM'S TOTAL LIABILITY SHALL NOT EXCEED $100. 
              SOME JURISDICTIONS DO NOT ALLOW THESE LIMITATIONS, SO THEY MAY NOT APPLY TO YOU.
            </p>
          </div>

          <h4 className="font-bold text-lg">13. Termination and Suspension</h4>
          <p>
            <strong>13.1</strong> You may terminate your registration at any time.<br/>
            <strong>13.2</strong> The Platform may suspend or terminate your account for:<br/>
          </p>
          <ul className="list-disc ml-8 space-y-1">
            <li>Violation of these Terms</li>
            <li>Receipt of credible complaints</li>
            <li>Safety concerns</li>
            <li>Inactivity exceeding 12 months</li>
            <li>Any reason at Platform's discretion</li>
          </ul>

          <h4 className="font-bold text-lg">14. Dispute Resolution</h4>
          <p>
            <strong>14.1</strong> Disputes shall be resolved through binding arbitration under AAA rules.<br/>
            <strong>14.2</strong> Arbitration shall occur in the Platform's state of incorporation.<br/>
            <strong>14.3</strong> Small claims court actions are exempt from arbitration.<br/>
            <strong>14.4</strong> Class action waiver: All claims must be brought individually.
          </p>

          <h4 className="font-bold text-lg">15. Governing Law</h4>
          <p>
            These Terms are governed by the laws of the United States and the state where the Platform 
            is incorporated, without regard to conflict of law principles.
          </p>

          <h4 className="font-bold text-lg">16. Reporting and Emergencies</h4>
          <div className="bg-red-50 border border-red-300 rounded-lg p-3">
            <p className="font-semibold text-red-900">In case of emergency:</p>
            <ul className="list-disc ml-6 space-y-1 text-red-800">
              <li>Call 911 (or local emergency services) immediately</li>
              <li>Notify airport/travel personnel</li>
              <li>Contact Traveler's emergency contacts if provided</li>
              <li>Do NOT attempt medical interventions unless certified</li>
              <li>Document the incident for your protection</li>
            </ul>
          </div>

          <h4 className="font-bold text-lg">17. Modifications to Terms</h4>
          <p>
            The Platform may modify these Terms with 30 days notice. Continued participation after 
            modifications constitutes acceptance of new Terms.
          </p>

          <h4 className="font-bold text-lg">18. Severability and Entire Agreement</h4>
          <p>
            If any provision is invalid, the remaining provisions continue in effect. These Terms 
            constitute the entire agreement between you and the Platform.
          </p>

          <div className="bg-purple-100 border-2 border-purple-400 rounded-lg p-4 mt-6">
            <p className="font-bold text-purple-900 text-center text-lg">
              ⚠️ ACKNOWLEDGMENT OF RISKS ⚠️
            </p>
            <p className="text-purple-800 mt-2 text-center">
              By registering as a Travel Companion, you acknowledge that you are interacting with 
              strangers and accept all associated risks. You understand that the Platform does not 
              screen Travelers or guarantee your safety. You participate voluntarily and at your own risk.
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6 border-t">
        <button
          onClick={() => setShowTermsModal(false)}
          className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

// Search Page Component
const SearchPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [customAirportCode, setCustomAirportCode] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [searchForm, setSearchForm] = useState({
    fromAirport: '',
    toAirport: '',
    startDate: '',
    endDate: ''
  });

  const indianAirports = [
    { code: 'DEL', name: 'Delhi - Indira Gandhi International', country: 'India' },
    { code: 'BOM', name: 'Mumbai - Chhatrapati Shivaji International', country: 'India' },
    { code: 'BLR', name: 'Bengaluru - Kempegowda International', country: 'India' },
    { code: 'MAA', name: 'Chennai - Chennai International', country: 'India' },
    { code: 'CCU', name: 'Kolkata - Netaji Subhas Chandra Bose International', country: 'India' },
    { code: 'HYD', name: 'Hyderabad - Rajiv Gandhi International', country: 'India' },
    { code: 'COK', name: 'Kochi - Cochin International', country: 'India' },
    { code: 'AMD', name: 'Ahmedabad - Sardar Vallabhbhai Patel International', country: 'India' },
    { code: 'GOI', name: 'Goa - Dabolim Airport', country: 'India' },
    { code: 'JAI', name: 'Jaipur - Jaipur International', country: 'India' }
  ];

  const usAirports = [
    { code: 'JFK', name: 'New York - John F. Kennedy International', country: 'USA' },
    { code: 'LAX', name: 'Los Angeles - Los Angeles International', country: 'USA' },
    { code: 'ORD', name: 'Chicago - O\'Hare International', country: 'USA' },
    { code: 'SFO', name: 'San Francisco - San Francisco International', country: 'USA' },
    { code: 'ATL', name: 'Atlanta - Hartsfield-Jackson International', country: 'USA' },
    { code: 'DFW', name: 'Dallas - Dallas/Fort Worth International', country: 'USA' },
    { code: 'SEA', name: 'Seattle - Seattle-Tacoma International', country: 'USA' },
    { code: 'BOS', name: 'Boston - Logan International', country: 'USA' },
    { code: 'IAD', name: 'Washington DC - Dulles International', country: 'USA' },
    { code: 'EWR', name: 'Newark - Newark Liberty International', country: 'USA' },
    { code: 'MIA', name: 'Miami - Miami International', country: 'USA' },
    { code: 'LAS', name: 'Las Vegas - McCarran International', country: 'USA' }
  ];

  const allAirports = [...indianAirports, ...usAirports];

  const handleCustomAirportSubmit = () => {
    const code = customAirportCode.toUpperCase().trim();
    if (code && code.length === 3 && /^[A-Z]{3}$/.test(code)) {
      if (modalType === 'searchFrom') {
        setSearchForm({...searchForm, fromAirport: code});
      } else if (modalType === 'searchTo') {
        setSearchForm({...searchForm, toAirport: code});
      }
      setShowModal(false);
      setCustomAirportCode('');
    } else {
      alert('Please enter a valid 3-letter airport code (e.g., LAX, DEL)');
    }
  };

  const openCustomAirportModal = (type) => {
    setModalType(type);
    setCustomAirportCode('');
    setShowModal(true);
  };

  const handleSearch = async () => {
    if (!searchForm.fromAirport || !searchForm.toAirport || 
        !searchForm.startDate || !searchForm.endDate) {
      alert('Please fill in all fields');
      return;
    }

    if (searchForm.fromAirport === searchForm.toAirport) {
      alert('Departure and destination airports must be different');
      return;
    }

    if (new Date(searchForm.startDate) > new Date(searchForm.endDate)) {
      alert('Start date must be before or same as end date');
      return;
    }

    setLoading(true);
    setShowResults(false);

    try {
      const params = new URLSearchParams({
        fromAirport: searchForm.fromAirport,
        toAirport: searchForm.toAirport,
        startDate: searchForm.startDate,
        endDate: searchForm.endDate
      });

      const response = await fetch(`${API_BASE_URL}/search?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.companions || []);
        setShowResults(true);
      } else {
        alert('Error searching for companions');
      }
    } catch (error) {
      alert('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fixed formatDate function to handle timezone issues
  const formatDate = (dateString) => {
    // Parse the date string and extract just the date part
    const date = new Date(dateString);
    
    // If the date string includes timezone info (ends with 'Z'), handle UTC
    if (dateString.includes('T') && dateString.endsWith('Z')) {
      // Extract just the date part (YYYY-MM-DD) and create a local date
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-');
      const localDate = new Date(year, month - 1, day);
      
      return localDate.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    // For other date formats, use regular parsing
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-6xl mx-auto p-4 mt-8">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Find a Travel Companion</h2>
          <button 
            onClick={() => navigate('/home')}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
          >
            <Home className="w-4 h-4" />
            <span>Home</span>
          </button>
        </div>

        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="block text-sm font-semibold text-gray-700 mb-2">From Airport</div>
              <select
                value={searchForm.fromAirport}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    openCustomAirportModal('searchFrom');
                  } else {
                    setSearchForm({...searchForm, fromAirport: e.target.value});
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                required
              >
                <option value="">Select departure airport</option>
                <optgroup label="🇮🇳 India">
                  {indianAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="🇺🇸 USA">
                  {usAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <option value="custom">➕ Other Airport (Enter Code)</option>
              </select>
              {searchForm.fromAirport && !allAirports.find(a => a.code === searchForm.fromAirport) && (
                <p className="text-sm text-gray-600 mt-1">Custom airport: {searchForm.fromAirport}</p>
              )}
            </div>

            <div>
              <div className="block text-sm font-semibold text-gray-700 mb-2">To Airport</div>
              <select
                value={searchForm.toAirport}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    openCustomAirportModal('searchTo');
                  } else {
                    setSearchForm({...searchForm, toAirport: e.target.value});
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                required
              >
                <option value="">Select destination airport</option>
                <optgroup label="🇮🇳 India">
                  {indianAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="🇺🇸 USA">
                  {usAirports.map(airport => (
                    <option key={airport.code} value={airport.code}>
                      {airport.code} - {airport.name.replace(' - ', ' • ')}
                    </option>
                  ))}
                </optgroup>
                <option value="custom">➕ Other Airport (Enter Code)</option>
              </select>
              {searchForm.toAirport && !allAirports.find(a => a.code === searchForm.toAirport) && (
                <p className="text-sm text-gray-600 mt-1">Custom airport: {searchForm.toAirport}</p>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="block text-sm font-semibold text-gray-700 mb-2">From Date</div>
              <input
                type="date"
                value={searchForm.startDate}
                onChange={(e) => setSearchForm({...searchForm, startDate: e.target.value})}
                min={today}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>

            <div>
              <div className="block text-sm font-semibold text-gray-700 mb-2">To Date</div>
              <input
                type="date"
                value={searchForm.endDate}
                onChange={(e) => setSearchForm({...searchForm, endDate: e.target.value})}
                min={searchForm.startDate || today}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 mr-3"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Terms and Conditions for Searching Companions
                </button>
              </span>
            </label>
          </div>

          <button
            onClick={handleSearch}
            disabled={loading || !termsAccepted}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Searching...' : 'Search for Companions'}
          </button>
        </div>
      </div>

      {/* Search Results */}
      {showResults && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-6">Available Travel Companions</h2>
          
          {searchResults.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                No travel companions found for the selected route and dates.
              </p>
              <p className="text-gray-500 mt-2">
                Please try different dates or check back later.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {searchResults.map((companion) => (
                <div key={companion.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-gray-800">{companion.name}</h3>
                    <span className="text-gray-600 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {formatDate(companion.travel_date)}
                    </span>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Flight Number</p>
                        <p className="font-semibold flex items-center gap-2">
                          <Plane className="w-4 h-4 text-blue-600" />
                          {companion.flight_number}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">From</p>
                        <p className="font-semibold flex items-center gap-2">
                          <Globe className="w-4 h-4 text-green-600" />
                          {companion.from_airport}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">To</p>
                        <p className="font-semibold flex items-center gap-2">
                          <Globe className="w-4 h-4 text-red-600" />
                          {companion.to_airport}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="grid md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Contact Number</p>
                        <p className="text-lg font-bold text-blue-700 flex items-center gap-2">
                          <Phone className="w-5 h-5" />
                          {companion.phone}
                        </p>
                        {companion.preferred_contact_method && (
                          <p className="text-xs text-gray-600 mt-1">
                            Prefers: {companion.preferred_contact_method === 'call' ? 'Phone calls' : 'Text messages'}
                          </p>
                        )}
                      </div>
                    </div>
                    {companion.preferred_contact_hours && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Available Hours</p>
                        <p className="text-sm text-gray-700 bg-gray-100 rounded px-3 py-2">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {companion.preferred_contact_hours}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Custom Airport Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Plane className="w-6 h-6 text-blue-600" />
                  Enter Airport Code
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Please enter the 3-letter airport code (e.g., LAX, DEL, LHR)
                </p>
                <input
                  type="text"
                  value={customAirportCode}
                  onChange={(e) => setCustomAirportCode(e.target.value)}
                  placeholder="Enter airport code"
                  maxLength={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-center text-lg font-mono uppercase"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCustomAirportSubmit();
                    }
                  }}
                  autoFocus
                />
                <p className="text-sm text-gray-500 mt-2">
                  Examples: JFK (New York), LAX (Los Angeles), DEL (Delhi)
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomAirportSubmit}
                  disabled={!customAirportCode.trim() || customAirportCode.trim().length !== 3}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Airport
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal for Searchers */}
     {/* Terms Modal for Searchers */}
{/* Terms Modal for Searchers */}
{showTermsModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 transform transition-all max-h-[90vh] overflow-hidden flex flex-col">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-800">Terms of Service and User Agreement</h3>
          <button
            onClick={() => setShowTermsModal(false)}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
      </div>
      
      <div className="p-6 overflow-y-auto flex-grow">
        <div className="space-y-4 text-gray-700">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-amber-900">
              IMPORTANT NOTICE: This platform facilitates connections between independent individuals. 
              Users must be 18 years or older. By accessing this service, you agree to these Terms of Service, 
              last updated {new Date().toLocaleDateString()}.
            </p>
          </div>

          <h4 className="font-bold text-lg">1. Definitions and Platform Description</h4>
          <p>
            <strong>1.1</strong> The "Platform" refers to this website and associated services.<br/>
            <strong>1.2</strong> "Companions" are individuals who register to provide travel assistance.<br/>
            <strong>1.3</strong> "Searchers" are individuals seeking travel companions for elderly relatives.<br/>
            <strong>1.4</strong> The Platform operates as a connection service only and does not employ or control companions.
          </p>

          <h4 className="font-bold text-lg">2. Eligibility and Account Requirements</h4>
          <p>
            <strong>2.1</strong> All users must be at least 18 years of age.<br/>
            <strong>2.2</strong> Users must provide accurate, current, and complete information.<br/>
            <strong>2.3</strong> Users are responsible for maintaining account security.<br/>
            <strong>2.4</strong> One account per person; no automated or bot accounts permitted.<br/>
            <strong>2.5</strong> The Platform reserves the right to verify user identity and refuse or terminate service.
          </p>

          <h4 className="font-bold text-lg">3. User Verification Obligations</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="font-semibold text-blue-900 mb-2">Before ANY travel arrangement, Searchers MUST:</p>
            <ul className="list-disc ml-6 space-y-2 text-blue-800">
              <li>Verify companion's government-issued photo identification</li>
              <li>Conduct independent background verification</li>
              <li>Verify all travel documents and bookings independently</li>
              <li>Meet in-person or via video call before travel</li>
              <li>Obtain and verify multiple emergency contacts</li>
              <li>Share all companion details with multiple family members</li>
              <li>Ensure elderly traveler has capacity to consent to arrangements</li>
              <li>Obtain appropriate travel and medical insurance</li>
            </ul>
          </div>

          <h4 className="font-bold text-lg">4. Prohibited Persons and Activities</h4>
          <p className="font-semibold">The following persons are PROHIBITED from using this Platform:</p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Registered sex offenders or persons with crimes against children</li>
            <li>Persons convicted of elder abuse, neglect, or financial exploitation</li>
            <li>Persons with active warrants or under investigation for fraud</li>
            <li>Persons using false identities or misrepresenting qualifications</li>
          </ul>
          <p className="mt-2"><strong>Prohibited activities include:</strong> harassment, solicitation for illegal purposes, 
          financial scams, identity theft, and any form of abuse or exploitation.</p>

          <h4 className="font-bold text-lg">5. Companion Limitations and Disclaimers</h4>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="font-semibold text-red-900">Companions on this Platform are NOT:</p>
            <ul className="list-disc ml-6 space-y-1 text-red-800">
              <li>Licensed healthcare providers or medical professionals</li>
              <li>Certified caregivers or elder care specialists</li>
              <li>Employees or contractors of the Platform</li>
              <li>Insured or bonded through the Platform</li>
              <li>Background checked or verified by the Platform</li>
              <li>Authorized to make medical or financial decisions</li>
            </ul>
          </div>

          <h4 className="font-bold text-lg">6. Financial Arrangements and Fraud Prevention</h4>
          <p>
            <strong>6.1</strong> All financial arrangements are solely between users.<br/>
            <strong>6.2</strong> The Platform does not process payments or hold funds.<br/>
            <strong>6.3</strong> Users should be alert to common scams including:<br/>
          </p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Requests for advance payments or wire transfers</li>
            <li>Overpayment schemes</li>
            <li>Identity theft attempts</li>
            <li>Phishing for financial information</li>
          </ul>
          <p className="mt-2"><strong>6.4</strong> All agreements should be documented in writing with clear terms.</p>

          <h4 className="font-bold text-lg">7. Privacy and Data Protection</h4>
          <p>
            <strong>7.1</strong> User data is processed according to our Privacy Policy.<br/>
            <strong>7.2</strong> Users consent to data processing for Platform operations.<br/>
            <strong>7.3</strong> Contact information must only be used for intended travel arrangements.<br/>
            <strong>7.4</strong> Users must delete contact information if arrangements are cancelled.<br/>
            <strong>7.5</strong> The Platform may share user information with law enforcement when required by law 
            or to prevent harm.<br/>
            <strong>7.6</strong> We comply with applicable data protection laws including CCPA for California residents.
          </p>

          <h4 className="font-bold text-lg">8. Intellectual Property</h4>
          <p>
            <strong>8.1</strong> Users retain ownership of their submitted content.<br/>
            <strong>8.2</strong> Users grant the Platform a non-exclusive license to display user content.<br/>
            <strong>8.3</strong> Users must not infringe on others' intellectual property rights.
          </p>

          <h4 className="font-bold text-lg">9. Limitation of Liability</h4>
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
            <p className="text-sm">
              <strong>9.1</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM AND ITS OFFICERS, 
              DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.<br/><br/>
              
              <strong>9.2</strong> THE PLATFORM'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY THE 
              USER TO THE PLATFORM IN THE 12 MONTHS PRECEDING THE CLAIM.<br/><br/>
              
              <strong>9.3</strong> THESE LIMITATIONS DO NOT APPLY TO LIABILITY FOR GROSS NEGLIGENCE, 
              WILLFUL MISCONDUCT, OR WHERE PROHIBITED BY APPLICABLE LAW.<br/><br/>
              
              <strong>9.4</strong> Some jurisdictions do not allow limitation of certain warranties or 
              liabilities, so some of the above limitations may not apply to you.
            </p>
          </div>

          <h4 className="font-bold text-lg">10. Indemnification</h4>
          <p>
            Users agree to indemnify, defend, and hold harmless the Platform and its affiliates from any 
            claims, losses, damages, liabilities, and expenses (including attorneys' fees) arising from:
            (a) your use of the Platform, (b) your violation of these Terms, (c) your violation of any 
            third-party rights, or (d) any agreement or transaction between you and another user.
          </p>

          <h4 className="font-bold text-lg">11. Dispute Resolution</h4>
          <p>
            <strong>11.1</strong> Users agree to attempt good faith resolution before legal action.<br/>
            <strong>11.2</strong> Disputes shall be resolved through binding arbitration under the Federal Arbitration Act and the rules of the American Arbitration Association.<br/>
            <strong>11.3</strong> Arbitration shall occur in the county where the Platform's headquarters are located.<br/>
            <strong>11.4</strong> Users may opt-out of arbitration within 30 days of account creation by sending written notice.<br/>
            <strong>11.5</strong> Small claims court actions are not subject to arbitration.<br/>
            <strong>11.6</strong> Class action waiver: All claims must be brought in individual capacity, not as part of any class action.
          </p>

          <h4 className="font-bold text-lg">12. Governing Law and Jurisdiction</h4>
          <p>
            These Terms are governed by the laws of the United States and the State in which the Platform 
            operates, without regard to conflict of law principles. Any legal action not subject to 
            arbitration shall be brought in the federal or state courts located in the county of the 
            Platform's principal place of business.
          </p>

          <h4 className="font-bold text-lg">13. Modifications and Termination</h4>
          <p>
            <strong>13.1</strong> The Platform may modify these Terms with 30 days notice via email or Platform notification.<br/>
            <strong>13.2</strong> Continued use after modifications constitutes acceptance.<br/>
            <strong>13.3</strong> The Platform may suspend or terminate accounts for Terms violations.<br/>
            <strong>13.4</strong> Users may terminate their account at any time.<br/>
            <strong>13.5</strong> Sections 9, 10, 11, and 12 survive termination.
          </p>

          <h4 className="font-bold text-lg">14. Emergency Procedures</h4>
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
            <p className="font-semibold text-yellow-900">In case of emergency during travel:</p>
            <ul className="list-disc ml-6 space-y-1 text-yellow-800">
              <li>Contact local emergency services immediately (911 in US)</li>
              <li>Notify the elderly traveler's emergency contacts</li>
              <li>Contact relevant consulates for international travel</li>
              <li>The Platform does not provide emergency assistance</li>
            </ul>
          </div>

          <h4 className="font-bold text-lg">15. Severability and Entire Agreement</h4>
          <p>
            <strong>15.1</strong> If any provision is deemed invalid, the remaining provisions continue in effect.<br/>
            <strong>15.2</strong> These Terms constitute the entire agreement between users and the Platform.<br/>
            <strong>15.3</strong> No waiver of any provision shall be deemed a continuing waiver.
          </p>

          <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4 mt-6">
            <p className="font-bold text-red-900 text-center text-lg">
              ⚠️ FINAL WARNING ⚠️
            </p>
            <p className="text-red-800 mt-2 text-center">
              You are solely responsible for the safety and wellbeing of your elderly relative. 
              This Platform does not verify, endorse, or guarantee any companion. Failure to conduct 
              proper verification could result in serious harm. By proceeding, you acknowledge you 
              have read, understood, and agree to all these terms and accept all associated risks.
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6 border-t">
        <button
          onClick={() => setShowTermsModal(false)}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

// Main App Component with Router
const App = () => {
  const location = useLocation();
  
  // Redirect root to /home
  React.useEffect(() => {
    if (location.pathname === '/' || location.pathname === '') {
      window.history.replaceState({}, '', '/home');
    }
  }, [location.pathname]);

  const renderPage = () => {
    switch (location.pathname) {
      case '/register':
        return <RegisterPage />;
      case '/search':
        return <SearchPage />;
      case '/home':
      case '/':
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {renderPage()}
      <Footer />
    </div>
  );
};

const Root = () => (
  <Router>
    <App />
  </Router>
);

export default Root;